<template>
    <div class="item-form" :class="{ 'is-open': isOpen, 'is-dragging': isDragging }">
		<div class="item-form__header">
			<h3 @click="tab = 'wedges'" :class="{ 'is-active': tab === 'wedges' }">Wedges</h3>
			<h3 @click="tab = 'export'" :class="{ 'is-active': tab === 'export' }">Export</h3>
			<h3 @click="tab = 'import'" :class="{ 'is-active': tab === 'import' }">Import</h3>
			<button class="item-form__close" @click="$emit('close')">&times;</button>
		</div>

		<div v-if="tab === 'wedges'" class="item-form__content item-form__scroller">
			<VueDraggable
				v-model="items"
				ghostClass="ghost"
				item-key="id"
				handle=".thumb"
				@start="isDragging = true"
				@end="isDragging = false"
			>
				<template #item="{ index }">
					<ItemInput
						v-model="items[index]"
						:can-remove="items.length > 1"
						@remove="itemId => remove(itemId)"
					/>
				</template>
			</VueDraggable>

			<Btn @click="add" style="margin-top: 20px;">Add</Btn>
			<span class="alt-btn" @click="shuffleItems">Shuffle</span>
			<span class="alt-btn" @click="clearItems">Clear</span>
			<span class="alt-btn" @click="resetItems">Reset</span>

			<div class="item-form__predefined-selections">
				<span class="alt-btn" @click="setSelection('product')">Product circle</span>
				<span class="alt-btn" @click="setSelection('use')">Product: Use</span>
				<span class="alt-btn" @click="setSelection('land')">Product: Land</span>
			</div>
		</div>

		<div v-if="tab === 'export'" class="item-form__content">
			<div class="item-form__export">{{ itemsJson }}</div>
			<div style="position: relative; margin-top: 20px; display: inline-block;">
				<span class="tooltip" :class="{ 'is-visible': hasExported }">Copied to clipboard!</span>
				<Btn @click="makeExportCopy">Export to clipboard</Btn>
			</div>
		</div>

		<div v-if="tab === 'import'" class="item-form__content">
			<textarea v-model="importInput" class="import-textarea"></textarea>
			<Btn @click="importData" style="margin-top: 20px;">Import (overwrite)</Btn>
		</div>
	</div>
</template>

<script>
import cloneDeep from 'clone-deep'
import VueDraggable from 'vuedraggable'
import { ref, watch, computed } from 'vue'

import { startItems, addColor } from './config'
import ItemInput from './ItemInput.vue'
import Btn from './Btn.vue'

const getId = () => Math.random().toString(36).slice(2)
const shuffleArray = (arr) => {
	const clone = cloneDeep(arr)
	clone.sort(() => Math.random() - 0.5);
	return clone
}

export default {
  name: "ItemForm",
	components: {
		VueDraggable,
		ItemInput,
		Btn,
	},
    props: {
		isOpen: {
			type: Boolean,
			default: false,
		}
    },
    setup(_props, { emit }) {

		const tab = ref('wedges')

		const itemsFromStart = (requestedGroup) => {
			const shuffled = shuffleArray(startItems).map(item => ({ ...item, id: getId() }))
			const selection = requestedGroup
				? shuffled.filter(item => item.group.includes(requestedGroup))
				: shuffled
			return addColor(selection)
		}
		const items = ref(itemsFromStart())

		const itemsJson = computed(() => JSON.stringify(items.value))

		const remove = (itemId) => {
			items.value = addColor(items.value.filter(item => item.id !== itemId))
		}

		watch(items, () => {
			emit('update', items.value)
		}, { immediate: true })

		const add = () => {
			items.value = addColor([
				...items.value,
				{
					id: getId(),
					img: '',
				}
			])
		}

		const isDragging = ref(false)

		const shuffleItems = () => {
			items.value = addColor(shuffleArray(items.value))
		}

		const clearItems = () => {
			items.value = addColor([{ id: getId(), img: '/logo.png' }])
		}

		const resetItems = () => {
			items.value = itemsFromStart()
		}

		const setSelection = (group) => {
			items.value = itemsFromStart(group)
		}

		const isExporting = ref(false)
		const hasExported = ref(false)
		const makeExportCopy = () => {
			if (isExporting.value) return
			isExporting.value = true;
			try {
				navigator.clipboard.writeText(itemsJson.value);
				hasExported.value = true
				setTimeout(() => {
					hasExported.value = false
					isExporting.value = false
				}, 2000)
			} catch (e) { alert('Failed, copy manually')}
		}

		const importInput = ref('')
		const importData = () => {
			try {
				const parsed = JSON.parse(importInput.value)
				if (!Array.isArray(parsed)) throw new Error('Invalid import data (not an array)')
				if (parsed.some(item => !item.img)) throw new Error('Invalid import data (no img)')

				items.value = addColor([
					...parsed.map(item => ({
						...item,
						id: item.id || getId(),
					})),
				])
			} catch (e) { alert(e.message) }
		}

    return {
			items,
			add,
			isDragging,
			remove,
			tab,
			itemsJson,
			makeExportCopy,
			hasExported,
			importInput,
			importData,
			shuffleItems,
			clearItems,
			resetItems,
			setSelection,
    }
  },
};
</script>

<style scoped>
.item-form {
	position: fixed;
	top: 0px;
	right: 0px;
	width: 100%;
	max-width: 400px;
	height: 100vh;
	background: #FFF7DF;
	transform: translateX(400px);
	transition: transform 200ms ease-out, box-shadow 200ms ease-out;
	z-index: 12;
}
.item-form.is-open {
	transform: none;
	box-shadow: 0 0 20px 0 rgba(33, 43, 54, 0.45);
}

.item-form__header {
	height: 60px;
	padding: 20px;
	position: relative
}

.item-form__close {
	position: absolute;
	top: 7px;
	right: 20px;
	padding: 0;
	margin: 0px;
	background: none;
	border: none;
	font-size: 40px;
	cursor: pointer;
}

.item-form__header h3 {
	margin: 0;
	display: inline-block;
	opacity: 0.6;
	cursor: pointer;
}
.item-form__header h3 + h3 {
	margin-left: 20px;
}
.item-form__header h3.is-active {
	opacity: 1;
}

.item-form__scroller {
	height: calc(100vh - 60px);
	overflow-y: scroll;
}

.alt-btn {
	display: inline-block;
	cursor: pointer;
}
.alt-btn:not(:first-child) {
	margin-left: 15px;
}
.alt-btn:hover {
	text-decoration: underline;
}
.btn + .alt-btn {
	margin-left: 20px;
}

.item-form__content {
	padding: 20px;
}
.item-form__export {
	font-family: 'Courier New', Courier, monospace;
	font-size: 14px;
	border-radius: 4px;
	background: #FFEEBB;
	padding: 10px;
	word-break: break-all;
}

.item-form__predefined-selections {
	margin-top: 20px;
	border-top: 1px solid #dab956;
	padding-top: 20px;
}

.tooltip {
	position: absolute;
	padding: 5px 10px;
	background: #62583C;
	color: white;
	border-radius: 3px;
	top: -28px;
    left: 50%;
    transform: translateX(-50%);
    white-space: nowrap;
	opacity: 0;
	pointer-events: none;
}
.tooltip.is-visible {
	transform: translateX(-50%) translateY(-8px);
	transition: transform 200ms ease-out, opacity 100ms linear;
	opacity: 1;
}

.import-textarea {
	padding: 5px 10px;
	border-radius: 4px;
	border-color: #ccc;
	width: 100%;
	height: 200px;
}
</style>